import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, MapPin, Camera, ExternalLink, Gauge, Star, X } from 'lucide-react';
import { partnerAPI } from '../services/api';
import { getImageUrl } from '../config/runtime';
import PartnerAdventureMap from '../components/PartnerAdventureMap';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * /p/:slug — public partner page (Scan to Play).
 *
 * The venue's branded hub: its adventures as playable tiles (each links to
 * /play/:starterId, the QR target) and a live gallery of visitor discoveries.
 * Doubles as QR poster target, sales artifact, and the visual twin of the
 * monthly report.
 *
 * Chrome is i18n'd (namespace "partner", EN/DE) and follows the VISITOR's
 * language — a French tourist at a German zoo gets English chrome. Venue
 * content (name, description, guide texts) stays partner-authored.
 */

const DIFFICULTY_KEYS = ['easy', 'moderate', 'hard'];

const Stars = ({ rating, size = 13 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((value) => (
      <Star
        key={value}
        size={size}
        className={
          value <= Math.round(rating)
            ? 'text-amber-400 fill-amber-400'
            : 'text-white/20'
        }
      />
    ))}
  </span>
);

const PartnerPage = () => {
  const { slug } = useParams();
  const { t } = useTranslation('partner');
  const [data, setData] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await partnerAPI.getPage(slug);
        setData(response.data.data);
        try {
          const [galleryResponse, reviewsResponse] = await Promise.all([
            partnerAPI.getGallery(slug, 12),
            partnerAPI.getReviews(slug, 6),
          ]);
          setGallery(galleryResponse.data.data.images || []);
          setReviews(reviewsResponse.data.data);
        } catch (_) {
          // Gallery/reviews are decorative — the page works without them
        }
      } catch (err) {
        console.error('[PartnerPage] Failed to load:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  // This page is a QR target and sales artifact — the tab title and share
  // preview should carry the venue's name, not the generic app title.
  useEffect(() => {
    if (!data?.partner?.name) return undefined;
    const previousTitle = document.title;
    document.title = `${data.partner.name} — Okio`;
    const description = data.partner.description || `${data.partner.name} on Okio`;
    const setMeta = (attribute, key, content) => {
      let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', `${data.partner.name} — Okio`);
    setMeta('property', 'og:description', description);
    if (data.isDemo) {
      // Prospect demos live on tokenized URLs and must never get indexed
      setMeta('name', 'robots', 'noindex, nofollow');
    }
    return () => {
      document.title = previousTitle;
      if (data.isDemo) {
        document.head.querySelector('meta[name="robots"]')?.remove();
      }
    };
  }, [data]);

  // Close the lightbox with Escape
  useEffect(() => {
    if (!lightboxImage) return undefined;
    const onKey = (event) => event.key === 'Escape' && setLightboxImage(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxImage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-emerald-400/30 border-t-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-8 text-center">
        <h1 className="text-white text-xl font-semibold mb-2">{t('notFound')}</h1>
        <Link to="/welcome" className="text-emerald-400 text-sm hover:underline">
          okio.ai
        </Link>
      </div>
    );
  }

  const { partner, adventures } = data;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Sales-demo watermark (plan §7.3): honest "Vorschau", never
          impersonation — the page self-destructs after the demo expiry */}
      {data.isDemo && (
        <div className="bg-amber-500/15 border-b border-amber-400/30 px-4 py-2 text-center">
          <p className="text-amber-300 text-xs font-semibold tracking-wide">
            Vorschau — so könnte Ihre Okio-Seite aussehen ·{' '}
            <a href="mailto:hello@okio.ai" className="underline hover:text-amber-200">
              hello@okio.ai
            </a>
          </p>
        </div>
      )}
      {/* Owner preview of a not-yet-live venue (self-serve before plan
          activation): the world gets 404, the owner gets this ribbon */}
      {data.isPreview && !data.isDemo && (
        <div className="bg-sky-500/15 border-b border-sky-400/30 px-4 py-2 text-center">
          <p className="text-sky-300 text-xs font-semibold tracking-wide">
            {t('previewNote')}{' '}
            <Link to={`/p/${slug}/dashboard`} className="underline hover:text-sky-200">
              {t('previewCta')}
            </Link>
          </p>
        </div>
      )}
      {/* Visitor language picker — chrome only, venue content stays as written */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 flex justify-end">
        <LanguageSwitcher />
      </div>
      {/* Hero */}
      <header className="max-w-6xl mx-auto px-6 pt-8 pb-10 text-center">
        {partner.logoUrl && (
          <img
            src={getImageUrl(partner.logoUrl)}
            alt={partner.name}
            className="h-16 mx-auto mb-6 object-contain"
          />
        )}
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{partner.name}</h1>
        {partner.description && (
          <p className="text-white/60 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            {partner.description}
          </p>
        )}
        {partner.website && (
          <a
            href={partner.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-4 text-emerald-400/80 hover:text-emerald-300 text-sm"
          >
            {partner.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
            <ExternalLink size={13} />
          </a>
        )}
        {/* Said once here, not repeated on every card */}
        <p className="mt-6 text-white/35 text-xs">{t('noApp')}</p>
      </header>

      {/* Adventures — tile grid on desktop (3-up), single column on mobile */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {adventures.length === 0 ? (
          <p className="text-center text-white/40 py-12">{t('empty')}</p>
        ) : (
          <div
            className={`grid gap-5 md:grid-cols-2 ${
              adventures.length >= 3 ? 'lg:grid-cols-3' : 'lg:max-w-3xl lg:mx-auto'
            }`}
          >
            {adventures.map((adventure) => (
              <div
                key={adventure.id}
                className="flex flex-col rounded-3xl overflow-hidden bg-gray-900 border border-white/10 hover:border-white/20 transition-colors"
              >
                {adventure.images?.[0] && (
                  <div className="relative h-44">
                    <img
                      src={getImageUrl(adventure.images[0])}
                      alt={adventure.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
                  </div>
                )}
                <div className="flex flex-col flex-1 p-5 sm:p-6">
                  <h2 className="text-lg font-semibold mb-2">{adventure.name}</h2>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/50 text-xs mb-3">
                    {adventure.duration && (
                      <span className="inline-flex items-center gap-1">
                        <Clock size={13} />
                        ~{adventure.duration} {t('minutes')}
                      </span>
                    )}
                    {DIFFICULTY_KEYS.includes(adventure.difficulty) && (
                      <span className="inline-flex items-center gap-1">
                        <Gauge size={13} />
                        {t(`difficulty.${adventure.difficulty}`)}
                      </span>
                    )}
                    {adventure.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={13} />
                        {adventure.location}
                      </span>
                    )}
                  </div>
                  {adventure.description && (
                    <p className="text-white/60 text-sm leading-relaxed line-clamp-4 mb-5">
                      {adventure.description}
                    </p>
                  )}
                  <Link
                    to={`/play/${adventure.id}`}
                    className="mt-auto inline-block w-full text-center px-8 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors"
                  >
                    {t('play')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* The venue map — pins for on-location adventures ("kitchen-table
            map": choose by where, not just by card) */}
        <PartnerAdventureMap
          adventures={adventures}
          playLabel={t('play')}
          title={t('mapTitle')}
          anywhereLabel={t('mapAnywhere')}
          regionalLabel={t('mapRegional')}
        />

        {/* Visitor reviews — social proof from real sessions */}
        {reviews?.count > 0 && (
          <section className="mt-14">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h3 className="flex items-center gap-2 text-white/70 text-sm font-semibold uppercase tracking-wider">
                <Star size={15} />
                {t('reviewsTitle')}
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <Stars rating={reviews.average} />
                <span className="text-white font-semibold">{reviews.average}</span>
                <span className="text-white/40">
                  ({reviews.count} {t('reviewsCount')})
                </span>
              </div>
            </div>
            {reviews.reviews.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {reviews.reviews.map((review) => (
                  <div
                    key={review.id}
                    className="rounded-2xl bg-gray-900 border border-white/10 p-5"
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Stars rating={review.rating} />
                      {review.adventure && (
                        <span className="text-white/30 text-xs truncate">
                          {review.adventure}
                        </span>
                      )}
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed mb-2">
                      {review.comment}
                    </p>
                    <p className="text-white/35 text-xs">{review.reviewer}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Visitor gallery */}
        {gallery.length > 0 && (
          <section className="mt-14">
            <h3 className="flex items-center gap-2 text-white/70 text-sm font-semibold uppercase tracking-wider mb-4">
              <Camera size={15} />
              {t('gallery')}
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {gallery.map((image) => (
                <button
                  key={image.id}
                  type="button"
                  onClick={() => setLightboxImage(image)}
                  className="focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded-xl"
                >
                  <img
                    src={getImageUrl(image.url)}
                    alt={image.photographer ? `by ${image.photographer}` : 'Visitor photo'}
                    loading="lazy"
                    className="aspect-square w-full object-cover rounded-xl bg-gray-900 hover:opacity-90 transition-opacity"
                  />
                </button>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Gallery lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <img
            src={getImageUrl(lightboxImage.url)}
            alt={lightboxImage.photographer ? `by ${lightboxImage.photographer}` : 'Visitor photo'}
            className="max-w-full max-h-[80vh] object-contain rounded-2xl"
            onClick={(event) => event.stopPropagation()}
          />
          {lightboxImage.photographer && (
            <p className="mt-4 text-white/60 text-sm">
              {t('photoBy')} {lightboxImage.photographer}
            </p>
          )}
        </div>
      )}

      {/* Powered by */}
      <footer className="border-t border-white/5 py-8 text-center">
        <Link to="/welcome" className="text-white/40 text-sm hover:text-white/70 transition-colors">
          {t('poweredBy')} <span className="font-semibold text-emerald-400/70">Okio</span>
        </Link>
      </footer>
    </div>
  );
};

export default PartnerPage;
